/**
 * Afrikaans: Files
 *
 * What this file covers: Uploading files and previewing them (documents, images, audio, video).
 *
 * Tone: Short functional labels, like a file manager.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const files: Catalog = {
  'upload.prompt': 'Sleep ’n lêer hierheen, of klik om te blaai',
  'upload.library': 'Kies uit die biblioteek',
  'upload.library.title': 'Mediabiblioteek',
  'upload.library.search': 'Soek op lêernaam',
  'upload.library.empty': 'Nog niks hier nie. Laai ’n lêer op om dit hier te sien.',
  'upload.library.more': 'Laai meer',
  'upload.uploading': 'Besig om op te laai',
  'upload.replace': 'Vervang',
  'upload.rename': 'Hernoem',
  'upload.rename.title': 'Hernoem lêer',
  'upload.rename.placeholder': 'Lêernaam',
  'upload.copy': 'Kopieer skakel',
  'upload.copied': 'Gekopieer',
  'upload.select': 'Kies',
  'upload.delete': 'Vee permanent uit',
  'upload.delete.confirm': 'Vee hierdie lêer vir almal uit? Enigiets wat dit nog gebruik, sal breek.',
  'upload.filter.all': 'Alles',
  'upload.filter.image': 'Beelde',
  'upload.filter.video': 'Video',
  'upload.filter.document': 'Dokumente',
  'upload.error.tooLarge': 'Daardie lêer is te groot.',
  'upload.error.type': 'Daardie lêertipe word nie aanvaar nie.',
  'upload.error.infected': 'Daardie lêer het die virusskandering gedruip en is nie gestoor nie.',
  'upload.error.scanUnavailable': 'Die virusskandeerder is onbereikbaar. Probeer netnou weer.',
  'upload.error.notConfigured': 'Lêerberging is nie opgestel nie. ’n Operateur moet dit onder Instellings, Integrasies opstel.',
  'upload.error.generic': 'Die oplaai het misluk.',
  'fileViewer.close': 'Sluit',
  'fileViewer.back': 'Terug',
  'fileViewer.download': 'Laai af',
  'fileViewer.downloadOriginal': 'Laai oorspronklike af',
  'fileViewer.print': 'Druk',
  'fileViewer.share': 'Deel',
  'fileViewer.copyLink': 'Kopieer skakel',
  'fileViewer.copyName': 'Kopieer lêernaam',
  'fileViewer.copied': 'Gekopieer',
  'fileViewer.copyFailed': 'Kon nie kopieer nie',
  'fileViewer.openExternally': 'Maak ekstern oop',
  'fileViewer.refresh': 'Verfris',
  'fileViewer.refreshed': 'Herlaai. Niks het verander nie.',
  'fileViewer.refreshedNewer': '’n Nuwer weergawe het dié op die skerm vervang.',
  'fileViewer.details': 'Besonderhede',
  'fileViewer.hideDetails': 'Versteek besonderhede',
  'fileViewer.fullscreen': 'Volskerm',
  'fileViewer.exitFullscreen': 'Verlaat volskerm',
  'fileViewer.keyboardHelp': 'Sleutelbordkortpaaie',
  'fileViewer.more': 'Meer',
  'fileViewer.previous': 'Vorige lêer',
  'fileViewer.next': 'Volgende lêer',
  'fileViewer.position': '{index} van {count}',
  'fileViewer.local': 'Plaaslik',
  'fileViewer.remote': 'Afgeleë',
  'fileViewer.readOnly': 'Lees alleen',
  'fileViewer.retry': 'Probeer weer',
  'fileViewer.cancel': 'Kanselleer',
  'fileViewer.loadedState': 'Gelaai',
  'fileViewer.loadingState': 'Laai tans',
  'fileViewer.inspectingState': 'Ondersoek bron',
  'fileViewer.processingState': 'Verwerk tans',
  'fileViewer.errorState': 'Kon nie oopmaak nie',
  'fileViewer.progressOf': '{loaded} van {total}',
  'fileViewer.progressLoaded': '{loaded} gelaai',
  'fileViewer.viewerTitle': 'Lêerkyker',
  'fileViewer.untitled': 'Naamlose lêer',
  'fileViewer.familyPdf': 'PDF',
  'fileViewer.familyImage': 'Beeld',
  'fileViewer.familyVideo': 'Video',
  'fileViewer.familyAudio': 'Oudio',
  'fileViewer.familySpreadsheet': 'Sigblad',
  'fileViewer.familyPresentation': 'Aanbieding',
  'fileViewer.familyDocument': 'Dokument',
  'fileViewer.familyCode': 'Kode',
  'fileViewer.familyText': 'Teks',
  'fileViewer.familyArchive': 'Argief',
  'fileViewer.familyUnknown': 'Lêer',
  'fileViewer.detailFilename': 'Lêernaam',
  'fileViewer.detailTitle': 'Titel',
  'fileViewer.detailType': 'Tipe',
  'fileViewer.detailMime': 'MIME-tipe',
  'fileViewer.detailSize': 'Grootte',
  'fileViewer.detailSource': 'Bron',
  'fileViewer.detailHost': 'Gasheer',
  'fileViewer.detailModified': 'Laas gewysig',
  'fileViewer.detailEtag': 'ETag',
  'fileViewer.detailChecksum': 'Kontrolesom (SHA-256)',
  'fileViewer.computeChecksum': 'Bereken kontrolesom',
  'fileViewer.computingChecksum': 'Bereken tans…',
  'fileViewer.detailDimensions': 'Afmetings',
  'fileViewer.detailAspect': 'Aspekverhouding',
  'fileViewer.detailDuration': 'Duur',
  'fileViewer.detailResolution': 'Resolusie',
  'fileViewer.detailLines': 'Reëls',
  'fileViewer.detailEncoding': 'Enkodering',
  'fileViewer.detailLanguage': 'Taal',
  'fileViewer.detailValidation': 'Validering',
  'fileViewer.detailFrames': 'Rame',
  'fileViewer.detailAlpha': 'Deursigtigheid',
  'fileViewer.detailRenderer': 'Weergegee deur',
  'fileViewer.rendererNative': 'Hierdie blaaier',
  'fileViewer.rendererConverted': 'Omgeskakelde voorskou',
  'fileViewer.rendererNone': 'Nie weergegee nie',
  'fileViewer.yes': 'Ja',
  'fileViewer.no': 'Nee',
  'fileViewer.unknownValue': 'Onbekend',
  'fileViewer.errorNetwork': 'Die lêer kon nie afgehaal word nie. Kontroleer die verbinding en probeer weer.',
  'fileViewer.errorCors': 'Die lêer se gasheer laat nie hierdie bladsy toe om dit te lees nie. Maak dit ekstern oop of laai dit eerder af.',
  'fileViewer.errorHttp': 'Die bediener het {status} geantwoord.',
  'fileViewer.errorAuth': 'Jy mag nie hierdie lêer lees nie, of die aanmelding het verval.',
  'fileViewer.errorNotFound': 'Die lêer is nie gevind nie. Dit is dalk uitgevee, of die skakel is verkeerd.',
  'fileViewer.errorExpired': 'Hierdie skakel het verval. Vra vir ’n nuwe een.',
  'fileViewer.errorTooLarge': 'Hierdie lêer is {size}, bo die {limit} wat hierdie kyker sal laai. Laai dit eerder af.',
  'fileViewer.errorUnsupported': 'Hierdie blaaier kan nie hierdie soort lêer wys nie.',
  'fileViewer.errorDecode': 'Die lêer kon nie gedekodeer word nie. Dit is dalk beskadig.',
  'fileViewer.errorAborted': 'Die laai is gekanselleer.',
  'fileViewer.mismatchWarning': 'Die lêer se inhoud stem nie ooreen met sy verklaarde tipe nie. Dit word gewys as wat die grepe sê dit is.',
  'fileViewer.unsupportedTitle': 'Geen voorskou vir hierdie lêer nie',
  'fileViewer.unsupportedSpreadsheet': '’n Blaaier kan nie op sy eie ’n sigblad teken nie. Laai die oorspronklike af om dit in ’n sigbladprogram oop te maak.',
  'fileViewer.unsupportedPresentation': '’n Blaaier kan nie op sy eie ’n aanbieding teken nie. Laai die oorspronklike af om dit in ’n aanbiedingsprogram oop te maak.',
  'fileViewer.unsupportedDocument': '’n Blaaier kan nie op sy eie ’n Word-dokument teken nie. Laai die oorspronklike af om dit in ’n woordverwerker oop te maak.',
  'fileViewer.unsupportedArchive': 'Argiewe word nie hier oopgemaak nie. Laai dit af om die lêers uit te pak.',
  'fileViewer.unsupportedUnknown': 'Hierdie soort lêer het geen voorskou nie. Die oorspronklike is onaangeraak en kan afgelaai word.',
  'fileViewer.unsupportedPdfInline': 'Hierdie blaaier het geen ingeboude PDF-kyker nie. Laai die lêer af of maak dit ekstern oop.',
  'fileViewer.unsupportedMediaCodec': 'Hierdie blaaier kan nie hierdie media dekodeer nie. Laai dit af of maak dit ekstern oop.',
  'fileViewer.unsupportedHls': 'Hierdie blaaier kan nie HLS-strome sonder ’n spelerbiblioteek speel nie, en geen is hier geïnstalleer nie.',
  'fileViewer.convertedPreview': 'Omgeskakelde teksvoorskou',
  'fileViewer.convertedPreviewHint': 'Teks wat op die bediener onttrek is. Uitleg, beelde en formatering word nie gewys nie; die oorspronklike is onveranderd.',
  'fileViewer.loadConvertedPreview': 'Wys teksvoorskou',
  'fileViewer.convertedPreviewFailed': 'Die teksvoorskou kon nie gelaai word nie.',
  'fileViewer.hexPreview': 'Heks-voorskou',
  'fileViewer.hexPreviewHint': 'Die eerste grepe van die lêer, om te sien wat dit werklik is.',
  'fileViewer.showHexPreview': 'Wys heks-voorskou',
  'fileViewer.originalKept': 'Niks hier verander die oorspronklike lêer nie.',
  'fileViewer.imageFit': 'Pas',
  'fileViewer.imageFill': 'Vul',
  'fileViewer.imageActual': '1:1',
  'fileViewer.zoomIn': 'Zoem in',
  'fileViewer.zoomOut': 'Zoem uit',
  'fileViewer.rotateLeft': 'Draai links',
  'fileViewer.rotateRight': 'Draai regs',
  'fileViewer.flipHorizontal': 'Keer horisontaal om',
  'fileViewer.flipVertical': 'Keer vertikaal om',
  'fileViewer.resetView': 'Herstel aansig',
  'fileViewer.checkerboard': 'Wys deursigtigheidsrooster',
  'fileViewer.transformNote': 'Slegs aansig. Draai en omkeer verander niks in die lêer nie.',
  'fileViewer.copyImage': 'Kopieer beeld',
  'fileViewer.exportPng': 'Voer voorskou as PNG uit',
  'fileViewer.animatedNote': 'Hierdie formaat kan geanimeer wees. Die blaaier speel dit soos die lêer bepaal.',
  'fileViewer.svgNote': 'Gewys as ’n prent. Skripte en eksterne hulpbronne binne die SVG loop nooit nie.',
  'fileViewer.playbackSpeed': 'Spoed',
  'fileViewer.loop': 'Herhaal',
  'fileViewer.pictureInPicture': 'Prent-in-prent',
  'fileViewer.fitContain': 'Pas',
  'fileViewer.fitCover': 'Vul',
  'fileViewer.fitOriginal': 'Oorspronklik',
  'fileViewer.captureFrame': 'Vang raam vas',
  'fileViewer.quality': 'Gehalte',
  'fileViewer.qualityAuto': 'Outomaties',
  'fileViewer.live': 'Regstreeks',
  'fileViewer.bufferHealth': 'Buffer',
  'fileViewer.droppedFrames': 'Verlore rame',
  'fileViewer.bandwidth': 'Bandwydte',
  'fileViewer.protocol': 'Protokol',
  'fileViewer.hlsNative': 'HLS (ingebou in hierdie blaaier)',
  'fileViewer.hlsPlayer': 'HLS (spelerbiblioteek)',
  'fileViewer.mediaDiagnostics': 'Diagnostiek',
  'fileViewer.mediaPlaybackFailed': 'Terugspeel het misluk. Die blaaier kon nie hierdie lêer dekodeer nie.',
  'fileViewer.seekBack': '10 sekondes terug',
  'fileViewer.seekForward': '10 sekondes vorentoe',
  'fileViewer.playPause': 'Speel of pouseer',
  'fileViewer.wrapLines': 'Vou',
  'fileViewer.lineNumbers': 'Reëlnommers',
  'fileViewer.searchInFile': 'Soek in lêer',
  'fileViewer.searchMatches': '{current} van {total}',
  'fileViewer.searchNone': 'Geen treffers nie',
  'fileViewer.goToLine': 'Gaan na reël',
  'fileViewer.language': 'Taal',
  'fileViewer.viewRaw': 'Rou',
  'fileViewer.viewRendered': 'Voorskou',
  'fileViewer.viewGrid': 'Rooster',
  'fileViewer.prettyPrint': 'Netjies formateer',
  'fileViewer.validJson': 'Geldige JSON',
  'fileViewer.invalidJson': 'Ongeldige JSON by reël {line}, kolom {column}: {message}',
  'fileViewer.copyCode': 'Kopieer inhoud',
  'fileViewer.truncatedNote': 'Wys die eerste {shown} van {total}. Laai die lêer af vir die res.',
  'fileViewer.sandboxNote': 'Weergegee in ’n sandput: geen skripte, vorms of eksterne versoeke nie.',
  'fileViewer.noRendererNote': 'Geen weergewer is vir hierdie formaat geïnstalleer nie, so dit word as teks gewys.',
  'fileViewer.gridSummary': '{rows} rye, {columns} kolomme',
  'fileViewer.emptyText': 'Hierdie lêer is leeg.',
  'fileViewer.pickerTitle': 'Maak ’n lêer oop',
  'fileViewer.pickerFamilies': 'Ondersteunde lêerfamilies',
  'fileViewer.pickerFromDevice': 'Maak oop vanaf toestel',
  'fileViewer.pickerDrop': 'Laat ’n lêer hier val',
  'fileViewer.pickerOr': 'of',
  'fileViewer.pickerChoose': 'Kies vanaf toestel',
  'fileViewer.pickerFromLink': 'Maak oop vanaf skakel',
  'fileViewer.pickerUrl': 'Lêer-URL',
  'fileViewer.pickerUrlPlaceholder': 'https://',
  'fileViewer.pickerOpenLink': 'Maak skakel oop',
  'fileViewer.pickerSendCredentials': 'Stuur geloofsbriewe',
  'fileViewer.pickerSendCredentialsHint': 'Sluit hierdie werf se koekies by die versoek in. Slegs vir skakels op ’n gasheer wat dit verwag.',
  'fileViewer.pickerRemember': 'Onthou hierdie skakel',
  'fileViewer.pickerRecentLocal': 'Onlangse plaaslike lêers',
  'fileViewer.pickerRecentLinks': 'Onlangse afgeleë skakels',
  'fileViewer.pickerName': 'Naam',
  'fileViewer.pickerSize': 'Grootte',
  'fileViewer.pickerModified': 'Gewysig',
  'fileViewer.pickerLink': 'Skakel',
  'fileViewer.pickerLastOpened': 'Laas oopgemaak',
  'fileViewer.pickerLocalNote': 'Plaaslike lêers bly op hierdie toestel tensy jy dit deel.',
  'fileViewer.pickerRemoteNote': 'Die gasheer van ’n skakel kan hierdie bladsy weier, of vra vir ’n aanmelding.',
  'fileViewer.pickerSizeNote': 'Lêers tot {size} maak hier oop. Gebruik ’n skakel vir groter lêers.',
  'fileViewer.pickerReopenNote': '’n Blaaier kan nie self ’n toestellêer weer oopmaak nie. Kies dit weer om dit oop te maak.',
  'fileViewer.pickerInvalidUrl': 'Voer ’n volledige https://-skakel in.',
  'fileViewer.pickerNoRecent': 'Nog niks nie.',
  'fileViewer.pickerClearRecent': 'Maak skoon',
  'fileViewer.helpTitle': 'Sleutelbordkortpaaie',
  'fileViewer.helpEscape': 'Sluit, of verlaat volskerm',
  'fileViewer.helpArrows': 'Vorige of volgende lêer',
  'fileViewer.helpZoom': 'Zoem in of uit',
  'fileViewer.helpFit': 'Pas by aansig',
  'fileViewer.helpActual': 'Werklike grootte',
  'fileViewer.helpRotate': 'Draai kloksgewys',
  'fileViewer.helpSearch': 'Soek',
  'fileViewer.helpFullscreen': 'Volskerm',
  'fileViewer.helpDetails': 'Wys of versteek besonderhede',
  'fileViewer.helpSpace': 'Speel of pouseer',
  'fileViewer.helpSeek': 'Soek terug of vorentoe',
  'upload.view': 'Bekyk',
  'imageCropper.title': 'Sny die prent by',
  'imageCropper.instructions': 'Sleep die prent om dit te skuif, en zoem met die skuifbalk, die muiswiel of twee vingers. Met die sleutelbord skuif die pyltjiesleutels dit, en die plus- en minussleutels zoem.',
  'imageCropper.stage': 'Prent se posisie',
  'imageCropper.zoom': 'Zoem',
  'imageCropper.zoomIn': 'Zoem in',
  'imageCropper.zoomOut': 'Zoem uit',
  'imageCropper.zoomValue': '{value}%',
  'imageCropper.preview': 'Voorskou',
  'imageCropper.previewAlt': 'Hoe die klaar prent lyk',
  'imageCropper.output': 'Gestoor as ’n {width} × {height} {format}.',
  'imageCropper.fillNote': 'Ruimte om die prent, en enige deursigtige dele, word met die agtergrondkleur ingevul.',
  'imageCropper.reset': 'Herstel',
  'imageCropper.cancel': 'Kanselleer',
  'imageCropper.confirm': 'Gebruik hierdie prent',
  'imageCropper.loading': 'Maak die prent oop…',
  'imageCropper.decodeError': 'Hierdie prent kon nie oopgemaak word nie. Probeer ’n JPG-, PNG- of WebP-lêer.',
  'imageCropper.exportError': 'Die prent kon nie gemaak word nie. Probeer weer, of kies ’n ander prent.',
  'imageCropper.lowResolution': 'Die deel wat jy gekies het, is net {edge} pixels breed, minder as {min}, so dit sal vaag lyk. Zoem uit, of gebruik ’n groter prent as jy een het.'
};

export default files;
